import { useMutation, useQueryClient } from '@tanstack/react-query';
import { makeRemoteReopenQueueRequest } from 'main/factories/usecases/queue/remote-reopen-queue-request';
import { FIVE_SECONDS, TEN_SECONDS, TOAST_ERROR, TOAST_SUCCESS, showToast } from 'lib/utils';
import { AskRequest, AskRequestStatus } from '@hone-automation/common';
import { makeRemoteQueueRequestById } from 'main/factories/usecases/queue';
import { UIFlaggedTransaction } from 'types/askcustomer';
import { invalidateQueueRequestsForLocation } from './queueQueryKeys';

export const useReopenQueueRequestMutation = (
  status: AskRequestStatus,
  locationId: string,
  onSuccessCallback?: (trx: UIFlaggedTransaction | undefined) => void,
  setActiveTransaction?: (trx: AskRequest | undefined) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { trxId: string; comment: string }) => {
      const service = makeRemoteReopenQueueRequest(data.trxId);
      return service.post({ locationId, comment: data.comment });
    },
    onSuccess: async trx => {
      // Invalidate old query key format (used by useQueueRequestsQuery)
      await queryClient.invalidateQueries({ queryKey: ['queueRequests', status, locationId] });
      // Invalidate new query key format (used by useGetQueueRequestsQuery in QueueContext)
      invalidateQueueRequestsForLocation(queryClient, locationId);
      await queryClient.invalidateQueries({ queryKey: ['queueRequestsCount', locationId] });
      await queryClient.invalidateQueries({ queryKey: ['flaggedTransactions', locationId] });

      if (trx) {
        const getRequestByIdService = makeRemoteQueueRequestById((trx as AskRequest).id);
        const updatedTrx = await getRequestByIdService.get();

        setActiveTransaction?.(updatedTrx as AskRequest);
        showToast('Request has been reopened', TOAST_SUCCESS, FIVE_SECONDS);
        return;
      }

      onSuccessCallback?.(trx as UIFlaggedTransaction | undefined);
      showToast('Request Updated', TOAST_SUCCESS, FIVE_SECONDS);
    },
    onError: () => {
      showToast('Error while updating request', TOAST_ERROR, TEN_SECONDS);
    },
  });
};
